import React from "react";
import { Megaphone, X } from "lucide-react";
import { ModalOverlay } from "@/components/ModalOverlay";

export default function AnnouncementPopup({ announcement, index, total, onClose }) {
  const dialogRef = React.useRef(null);
  const contentRef = React.useRef(null);

  React.useLayoutEffect(() => {
    const previousFocus = document.activeElement;
    const dialog = dialogRef.current;
    const focusable = () => Array.from(dialog.querySelectorAll(
      'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]'
    )).filter(element => element.tabIndex >= 0
      && getComputedStyle(element).display !== "none"
      && getComputedStyle(element).visibility !== "hidden");
    const keepTabInside = event => {
      if (event.key !== "Tab") return;
      const elements = focusable();
      const first = elements[0];
      const last = elements[elements.length - 1];
      const active = document.activeElement;
      if (!first || !dialog.contains(active) || active === dialog
        || (event.shiftKey ? active === first : active === last)) {
        event.preventDefault();
        (event.shiftKey ? last : first)?.focus();
        if (!first) dialog.focus();
      }
    };
    const keepFocusInside = event => {
      if (!dialog.contains(event.target)) dialog.focus({ preventScroll: true });
    };
    document.addEventListener("keydown", keepTabInside, true);
    document.addEventListener("focusin", keepFocusInside);
    return () => {
      document.removeEventListener("keydown", keepTabInside, true);
      document.removeEventListener("focusin", keepFocusInside);
      if (previousFocus?.isConnected) previousFocus.focus?.({ preventScroll: true });
    };
  }, []);

  React.useLayoutEffect(() => {
    if (contentRef.current) contentRef.current.scrollTop = 0;
    dialogRef.current?.focus({ preventScroll: true });
  }, [announcement.id]);

  return (
    <ModalOverlay
      onClose={onClose}
      className="items-center justify-center bg-slate-950/55 p-4"
      style={{ zIndex: 100000 }}
    >
      <section
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-announcement-title"
        aria-describedby="login-announcement-message"
        data-testid="login-announcement-popup"
        className="relative flex max-h-[calc(100vh-2rem)] min-h-0 w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl focus:outline-none"
        style={{ maxHeight: "calc(100dvh - 2rem)" }}
      >
        <div className="h-1.5 shrink-0 bg-gradient-to-r from-blue-700 via-sky-500 to-cyan-400" />
        <button
          type="button"
          onClick={onClose}
          aria-label="Dismiss announcement"
          className="absolute right-4 top-4 z-10 rounded-lg bg-white/95 p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600"
        >
          <X size={18} />
        </button>
        <div
          ref={contentRef}
          tabIndex={0}
          role="region"
          aria-label="Announcement content"
          data-testid="announcement-content"
          className="min-h-0 overflow-y-auto overscroll-contain p-6 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-blue-600 sm:p-8"
        >
          <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
            <Megaphone size={22} />
          </div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-blue-700">
            VianBills announcement
          </div>
          <h2 id="login-announcement-title" className="break-words pr-8 text-xl font-bold text-slate-900 [overflow-wrap:anywhere] sm:text-2xl">
            {announcement.title}
          </h2>
          <p id="login-announcement-message" className="mt-4 whitespace-pre-wrap break-words text-sm leading-6 text-slate-700 [overflow-wrap:anywhere]">
            {announcement.message}
          </p>
        </div>
          <div className="flex shrink-0 items-center justify-between gap-4 border-t border-slate-100 px-6 py-5 sm:px-8">
            <span aria-live="polite" className="text-xs text-slate-500">
              {total > 1 ? `Announcement ${index + 1} of ${total}` : "Shown at login"}
            </span>
            <button
              type="button"
              onClick={onClose}
              className="f-btn f-btn-primary"
              data-testid="dismiss-login-announcement"
            >
              Got it
            </button>
          </div>
      </section>
    </ModalOverlay>
  );
}
